import { useMemo, useState } from "react";
import type { ComponentType, SVGProps } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs } from "@/components/ui/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RulesApiFactory } from "@/apis/research/rules";
import { apiRuleWhenSummaryLine } from "@/components/ai-consumer-research/brand-moderator/rules/api-rule-to-draft";
import { CHANNEL_LABELS, displayActionModeLabel, displayChannelLabel, displayRuleTriggerLabel, type Channel } from "@/components/ai-consumer-research/brand-moderator/rules/rule-builder-flow";
import { FlipkartIcon } from "@/components/icons/flipkart";
import { GoogleIcon } from "@/components/icons/google";
import { InstagramIcon } from "@/components/icons/instagram";
import { MetaIcon } from "@/components/icons/meta";
import { RedditIcon } from "@/components/icons/reddit";
import { TikTokIcon } from "@/components/icons/tiktok";
import { TwitterIcon } from "@/components/icons/twitter";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useNavigate } from "@tanstack/react-router";
import { Globe, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { unwrapApiError } from "@/lib/error";
import { MyntraIcon } from "@/components/icons/myntra";

type RuleStatus = "Active" | "Paused";

type RuleListItemModel = {
  id: string;
  name: string;
  whenSummary: string;
  channels: string[];
  triggers: number;
  status: RuleStatus;
  accent: "red" | "amber" | "blue";
};

type Tab = "All" | "Active" | "Paused";

type RulesChannelIcon = ComponentType<SVGProps<SVGSVGElement>>;

const RULE_ROW_CHANNEL_ICONS: Record<Channel, RulesChannelIcon> = {
  amazon: MetaIcon,
  flipkart: FlipkartIcon,
  myntra: MyntraIcon,
  reddit: RedditIcon,
  youtube: GoogleIcon,
  x: TwitterIcon,
  instagram: InstagramIcon,
  tiktok: TikTokIcon,
};

function channelSlugForPillLabel(label: string): Channel | null {
  if (label in CHANNEL_LABELS) return label as Channel;
  const found = (Object.entries(CHANNEL_LABELS) as [Channel, string][]).find(([, name]) => name === label);
  return found?.[0] ?? null;
}

function RuleChannelPillIcon({ label }: { label: string }) {
  const fallbackClass = "size-5 shrink-0 text-muted-foreground";
  const slug = channelSlugForPillLabel(label);

  if (!slug) {
    return <Globe className={`${fallbackClass} opacity-60`} aria-hidden />;
  }
  const Icon = RULE_ROW_CHANNEL_ICONS[slug];
  return <Icon className="size-5 shrink-0" aria-hidden />;
}

function StatusBadge({ status }: { status: RuleStatus }) {
  if (status === "Active") {
    return <Badge className="rounded-full bg-success/10 text-success px-2 py-1 text-xs font-semibold">Active</Badge>;
  }
  return <Badge className="rounded-full bg-muted text-muted-foreground px-2 py-1 text-xs font-semibold">Paused</Badge>;
}

function RuleRow({ rule }: { rule: RuleListItemModel }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPaused = rule.status === "Paused";

  const goToEdit = () => {
    navigate({ to: "/dashboard/consumer-research/rules/update/$id", params: { id: rule.id } });
  };

  const {
    mutate: deleteRule,
    isPending,
    variables: deletingId,
  } = useMutation({
    mutationFn: (ruleId: string) => RulesApiFactory.Apis.DeleteRuleById(ruleId),
    onSuccess: async (_data, ruleId) => {
      await RulesApiFactory.Invalidations.AfterDeleteRuleById(queryClient, ruleId);
      toast.success("Rule deleted");
    },
    onError: (error) => {
      toast.error(unwrapApiError(error));
    },
  });

  const isDeletingThis = isPending && deletingId === rule.id;

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Edit rule: ${rule.name}`}
      className={cn("cursor-pointer rounded-2xl border bg-card px-4 py-4 hover:border-blue-300 transition ", isPaused && "opacity-60")}
      onClick={goToEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          goToEdit();
        }
      }}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2">
        <div className="flex justify-end pt-0.5">
          <StatusBadge status={rule.status} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{rule.name}</div>
          <div className="mt-1 text-xs text-muted-foreground truncate">{rule.whenSummary}</div>
        </div>
        <div className="flex shrink-0 items-center justify-self-end gap-9">
          <div className="flex flex-wrap items-center justify-end gap-3">
            {rule.channels.map((ch) => (
              <RuleChannelPillIcon label={ch} />
            ))}
          </div>
          <div className="w-[64px] shrink-0 text-center">
            <div className="text-sm font-semibold text-foreground leading-none">{rule.triggers}</div>
            <div className="mt-1 text-[10px] font-semibold text-muted-foreground tracking-wide uppercase">TRIGGERS</div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
            disabled={isDeletingThis}
            aria-busy={isDeletingThis}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteRule(rule.id);
            }}
          >
            {isDeletingThis ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Trash2 />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RuleRowSkeleton() {
  return (
    <div className="rounded-2xl border bg-card px-4 py-4">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-4 gap-y-2">
        <div className="flex w-[72px] justify-end pt-0.5">
          <Skeleton className="h-7 w-[72px] rounded-full" />
        </div>
        <div className="min-w-0 space-y-2">
          <Skeleton className="h-4 w-[min(100%,280px)]" />
          <Skeleton className="h-3 w-[min(100%,420px)]" />
        </div>
        <div className="flex shrink-0 items-center justify-self-end gap-6">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-14 rounded-full" />
          </div>
          <div className="w-[64px] space-y-2 text-right">
            <Skeleton className="ml-auto h-4 w-8" />
            <Skeleton className="ml-auto h-2.5 w-14" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RulesListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <section className="space-y-3" aria-busy="true" aria-label="Loading rules">
      <div className="flex items-center gap-2">
        <Skeleton className="h-7 w-[72px] rounded-full" />
        <Skeleton className="h-7 w-[88px] rounded-full" />
        <Skeleton className="h-7 w-[92px] rounded-full" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, idx) => (
          <RuleRowSkeleton key={idx} />
        ))}
      </div>
    </section>
  );
}

function RulesList({ data }: { data: RuleListItemModel[] }) {
  const [tab, setTab] = useState<Tab>("All");

  // const counts = useMemo(() => {
  //   const active = data.filter((r) => r.status === "Active").length;
  //   const paused = data.filter((r) => r.status === "Paused").length;
  //   return { all: data.length, active, paused };
  // }, [data]);

  const filtered = useMemo(() => {
    if (tab === "Active") return data.filter((r) => r.status === "Active");
    if (tab === "Paused") return data.filter((r) => r.status === "Paused");
    return data;
  }, [data, tab]);

  return (
    <section>
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="gap-3">
        <div className="flex items-end gap-2">
          {/* <TabsList className="inline-flex h-auto flex-nowrap items-end gap-2 bg-transparent p-0">
            {RULES_FILTER_TABS.map(({ value, label, countKey }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:border-foreground data-[state=active]:shadow-none whitespace-nowrap"
              >
                <span className="flex items-center gap-1.5">
                  <span>{label}</span>
                </span>
              </TabsTrigger>
            ))}
          </TabsList> */}
          <Link to="/dashboard/consumer-research/rules" className="ml-auto shrink-0">
            <Button>
              <Plus /> Create Rule
            </Button>
          </Link>
        </div>

        <div className="space-y-3">
          {filtered.map((rule) => (
            <RuleRow key={rule.id} rule={rule} />
          ))}
        </div>
      </Tabs>
    </section>
  );
}

export function RulesEngine() {
  const { data: rulesResponse, isLoading } = useQuery(RulesApiFactory.Queries.GetRulesList());

  const rules: RuleListItemModel[] = useMemo(() => {
    const apiRules = rulesResponse?.data?.rules ?? [];
    if (apiRules.length === 0) return [];

    return apiRules.map((r, idx) => {
      const status: RuleStatus = r.deleted_at ? "Paused" : "Active";
      const accent: RuleListItemModel["accent"] = idx % 3 === 0 ? "red" : idx % 3 === 1 ? "amber" : "blue";
      const channels = r.platform.length ? r.platform.map(displayChannelLabel) : ["All channels"];

      return {
        id: r.id,
        name: displayRuleTriggerLabel(r.trigger),
        whenSummary: `WHEN ${apiRuleWhenSummaryLine(r)} → ${displayActionModeLabel(r.perform)}`,
        channels,
        triggers: 0,
        status,
        accent,
      };
    });
  }, [rulesResponse]);

  if (isLoading) {
    return <RulesListSkeleton />;
  }

  if (rules.length === 0) {
    return <p className="text-sm text-muted-foreground">You don&apos;t have any rules yet.</p>;
  }

  return <RulesList data={rules} />;
}
